<template>
  <v-dialog v-model="addDialog" class="the-app-dialogs" persistent max-width="600px">
    <v-card>
      <form @submit.prevent="validateBeforeSubmit($event)">
        <v-layout row class="no-select" style="background-color:#09132f">
          <h3 id="dialog-title" class="text-uppercase px-3 pt-3" style="text-xs-center letter-spacing:2px">
            <v-layout align-center>
              <v-icon light x-large class="rgba(0,0,0,0.3)--text pb-1">attach_money</v-icon>
              <span class="px-3">{{ $t('Add New Bank Transaction') }}</span>
            </v-layout>
          </h3>

          <v-spacer />

          <v-card-actions>
            <v-btn fab icon dark @click="$emit('hidemodal')">
              <v-icon>close</v-icon>
            </v-btn>
          </v-card-actions>
        </v-layout>

        <v-card-title class="grey lighten-3">
          <v-layout row wrap>
            <v-flex xs6 pa-2>
              <v-text-field
                :value="bankInfo.balance"
                :label="`${$t('Balance Before Transaction')} *`"
                name="balance_before_transaction"
                box
                hide-details
                readonly
                background-color="white"
                :error="errors.has('balance_before_transaction')"
                v-validate="'required'"
              />
            </v-flex>

            <v-flex xs6 pa-2>
              <v-menu v-model="dateMenu" full-width max-width="290" :close-on-content-click="false">
                <v-text-field
                  slot="activator"
                  :value="form.date"
                  :label="`${$t('Date')} *`"
                  prepend-inner-icon="event"
                  box
                  readonly
                  hide-details
                  name="transaction_date"
                  background-color="white"
                  autocomplete="off"
                  :error="errors.has('transaction_date')"
                  v-validate="'required|date_format:yyyy-MM-dd'"
                />
                <v-date-picker @change="dateMenu = false" v-model="form.date" />
              </v-menu>
            </v-flex>

            <v-flex xs11 :class="{ xs12: !hasPermissions(['users_store']) }" pa-2>
              <v-autocomplete
                id="bank-transaction-focus-me"
                :items="fullStaffList"
                v-model="form.depositor_id_fk"
                :label="`${$t('Depositor Name')} *`"
                box
                hide-details
                :error="errors.has('depositor_id_fk')"
                background-color="white"
                v-validate="'required'"
                data-vv-name="depositor_id_fk"
                :menu-props="{ transition: 'slide-y-transition' }"
              />
            </v-flex>

            <v-flex xs1 pt-2 v-if="hasPermissions(['users_store'])">
              <v-tooltip top>
                <v-btn label @click="$emit('add-staff')" slot="activator" icon fab small color="green">
                  <v-icon color="white" size="25">add</v-icon>
                </v-btn>
                <span>{{ $t('Add new staff') }}</span>
              </v-tooltip>
            </v-flex>

            <v-flex xs12 pa-2>
              <p>{{ `${$t('Transaction Type')} *` }}</p>
              <v-radio-group
                v-model="form.transaction_type"
                name="transaction_type"
                v-validate="'required'"
                row
                @change="newBalance"
              >
                <v-radio :label="$t('Withdraw')" value="Withdraw" color="blue" />
                <v-radio :label="$t('Deposit')" value="Deposit" color="blue" />
              </v-radio-group>
            </v-flex>

            <v-flex xs6 pa-2>
              <v-text-field
                v-model="form.amount"
                :label="`${$t('Amount')} *`"
                name="amount"
                box
                hide-details
                background-color="white"
                :error="errors.has('amount')"
                v-validate="'required|decimal|min_value:0'"
                @input="newBalance"
              />
            </v-flex>

            <v-flex xs6 pa-2>
              <v-text-field
                v-model="form.balance_after_transaction"
                :label="`${$t('Balance After Transaction')} *`"
                name="balance_after_transaction"
                box
                hide-details
                readonly
                background-color="white"
                :error="errors.has('balance_after_transaction')"
                v-validate="'required'"
              />
            </v-flex>
          </v-layout>
        </v-card-title>

        <v-divider light />

        <v-card-actions class="grey lighten-3">
          <v-layout column>
            <v-flex xs12 class="pb-2 px-2" style="color: #444;"
              >- {{ $t('Fields marked with asterisk ( * ) are required.') }}</v-flex
            >

            <v-layout justify-end align-center pr-2 pl-2>
              <v-checkbox color="primary" v-model="keepForm" :label="$t('Keep this form to add another one.')" />

              <v-btn outline color="primary" @click="$emit('hidemodal')">{{ $t('Cancel') }}</v-btn>
              <v-btn type="submit" color="success" :loading="btnAddLoading">
                <v-icon :right="$vuetify.rtl" :left="!$vuetify.rtl">save</v-icon>
                {{ $t('Add') }}
              </v-btn>
            </v-layout>
          </v-layout>
        </v-card-actions>
      </form>
    </v-card>
  </v-dialog>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import * as helpers from '@/mixins/helperFunctions.js'

export default {
  name: 'add-component',
  props: {
    addDialog: { type: Boolean, required: true },
    bankInfo: { type: Object, required: true }
  },

  data() {
    return {
      dateMenu: false,
      btnAddLoading: false,
      form: {
        date: '',
        transaction_type: 'Withdraw',
        amount: 0,
        balance_after_transaction: '',
        balance_before_transaction: '',
        depositor_id_fk: null
      },
      keepForm: false
    }
  },

  computed: {
    ...mapGetters({
      fullStaffList: 'staff/getFullStaffsList'
    })
  },

  watch: {
    addDialog: {
      immediate: false,
      handler() {
        if (this.addDialog) {
          this.addEscKeyListener()
          this.resetForm()
          this.keepForm = false
        }
      }
    }
  },

  methods: {
    ...mapActions({
      addBank: 'bankTransactions/addTransaction'
    }),

    hasPermissions(permissions) {
      return helpers.userHasPermissions(permissions)
    },

    addEscKeyListener() {
      document.addEventListener('keyup', e => {
        // if the "ESC" button was pressed and this dialog is shown, hide it
        if (e.keyCode === 27 && this.addDialog) {
          this.$emit('hidemodal')
        }
      })
    },

    resetForm() {
      this.$validator.pause()
      for (let key in this.form) {
        this.form[`${key}`] = null
      }
      this.form.date = this.$moment()
        .format()
        .substr(0, 10)

      this.$nextTick(() => {
        this.$validator.resume()
        // default veutify autofocus only one time works so we have to handled in this way.
        // document.getElementById('bank-transaction-focus-me').focus()
      })
    },

    newBalance() {
      const amount = this.form.transaction_type === 'Withdraw' ? -this.form.amount : this.form.amount
      this.form.balance_after_transaction = Number(amount) + Number(this.bankInfo.balance)
    },

    validateBeforeSubmit(event) {
      this.$validator
        .validateAll()
        .then(() => {
          if (!this.errors.any()) {
            this.btnAddLoading = true

            let formData = new FormData(event.target)
            formData.append('bank_id_fk', this.bankInfo.bank_id)
            formData.append('depositor_id_fk', this.form.depositor_id_fk)

            // for (let pair of formData.entries()) {
            //   console.warn(`${pair[0]}: ${pair[1]}`)
            // }

            this.addBank(formData)
              .then(() => {
                this.btnAddLoading = false
                this.$emit('reset-datatable')
                if (this.keepForm) {
                  this.resetForm()
                } else {
                  this.$emit('hidemodal')
                }
                this.fetchLastDollarPrice()
              })
              .catch(() => {
                this.btnAddLoading = false
              })
          }
        })
        .catch(() => {
          this.btnAddLoading = false
        })
    }
  }
}
</script>

<style scoped />
